import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Banknote, CheckCircle2, Clock, TrendingUp, Users } from "lucide-react";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

const TRANSFER_TYPES = [
  { value: "outgoing", label: "Lähtevä" },
  { value: "incoming", label: "Saapuva" },
];
const STATUS = [
  { value: "pending", label: "Odottaa" },
  { value: "processing", label: "Käsittelyssä" },
  { value: "completed", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
  { value: "failed", label: "Epäonnistui" },
];
const PAY_METHODS = [
  { value: "cash", label: "Käteinen" },
  { value: "card", label: "Kortti" },
  { value: "bank", label: "Tilisiirto" },
];

export default function MoneyTransferDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/money-transfer/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const config = {
    endpoint: "/money-transfer",
    testidPrefix: "mt",
    emptyMessage: "Ei rahansiirtoja",
    defaults: { type: "outgoing", currency: "EUR", amount: 0, fee: 0, payment_method: "cash", status: "pending" },
    fields: [
      { key: "type", label: "Tyyppi", type: "select", options: TRANSFER_TYPES },
      { key: "sender_name", label: "Lähettäjä", type: "text" },
      { key: "sender_phone", label: "Lähettäjän puhelin", type: "text" },
      { key: "sender_id_number", label: "Lähettäjän henkilötunnus", type: "text" },
      { key: "recipient_name", label: "Vastaanottaja", type: "text", required: true },
      { key: "recipient_phone", label: "Vastaanottajan puhelin", type: "text" },
      { key: "recipient_city", label: "Kaupunki", type: "text" },
      { key: "recipient_country", label: "Maa", type: "text" },
      { key: "amount", label: "Summa", type: "number" },
      { key: "currency", label: "Valuutta", type: "text" },
      { key: "fee", label: "Palvelumaksu €", type: "number" },
      { key: "payment_method", label: "Maksutapa", type: "select", options: PAY_METHODS },
      { key: "status", label: "Tila", type: "select", options: STATUS },
      { key: "office_location", label: "Toimisto", type: "text" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "transfer_number", label: "Nro" },
      { key: "type", label: "Tyyppi", format: "badge" },
      { key: "recipient_name", label: "Vastaanottaja" },
      { key: "recipient_country", label: "Maa" },
      { key: "amount", label: "Summa", format: "eur", align: "right" },
      { key: "fee", label: "Maksu", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="money-transfer-dashboard">
      <PageHeader kicker="Sovellus" title="Rahansiirto" description="Lähetä ja vastaanota rahaa, hallinnoi siirtoja." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Banknote className="w-5 h-5" />} label="Siirrot yht." value={stats?.transfers_total ?? 0} loading={loading} testid="mt-kpi-total" />
        <KpiCard icon={<Clock className="w-5 h-5" />} label="Odottaa" value={stats?.pending ?? 0} accent="text-amber-300" loading={loading} testid="mt-kpi-pending" />
        <KpiCard icon={<CheckCircle2 className="w-5 h-5" />} label="Valmiit" value={stats?.completed ?? 0} accent="text-emerald-300" loading={loading} testid="mt-kpi-completed" />
        <KpiCard icon={<TrendingUp className="w-5 h-5" />} label="Palvelutuotot" value={formatEur(stats?.fee_revenue)} accent="text-amber-300" loading={loading} testid="mt-kpi-fees" />
      </div>
      <Tabs defaultValue="transfers">
        <TabsList className="bg-amber-500/10 mb-4">
          <TabsTrigger value="transfers" data-testid="mt-tab-transfers">Siirrot</TabsTrigger>
          <TabsTrigger value="staff" data-testid="mt-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>
        <TabsContent value="transfers">
          <Panel title="Siirrot">
            <CrudList config={config} />
          </Panel>
        </TabsContent>
        <TabsContent value="staff">
          <ModuleStaffTab moduleKey="money_transfer" moduleLabel="Rahansiirto" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
